import React, { useState, useEffect } from "react";
import "../styles/contactForm.css";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { createBooking, resetState } from "../features/booking/bookingSlice";
import { toast } from "react-toastify";

const steps = [
  { name: "name", label: "Let’s schedule a quick call, firstly, what’s your name?", type: "text" },
  { name: "email", label: "What’s your email?", type: "email" },
  { name: "services", label: "What help do you need?", type: "checkbox", options: ["Digital Marketing", "Website development", "Invoice generator"] },
  { name: "urgency", label: "How urgent do you need this?", type: "radio", options: ["Urgently", "This month", "Next 2–3 months"] },
  { name: "budget", label: "What’s your budget range?", type: "radio", options: ["$500 – $1000", "$1000 – $2000", "$2000 – $3000", "$3000 – $5000", "$5000 – $10000", "$10000+"] },
  { name: "notes", label: "Almost done, Mankug. Anything else you’d like us to know?", type: "textarea" },
  { name: "schedule", label: "Let’s book a time to chat! Please select a day/time.", type: "datetime" }
];

const BookingForm = () => {
  const dispatch = useDispatch();
  const { isLoading, isSuccess, isError, message } = useSelector((state) => state.booking);

  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    services: [],
    urgency: "",
    budget: "",
    notes: "",
    schedule: { date: "", time: "" },
  });

  // Load saved data
  useEffect(() => {
    const savedData = localStorage.getItem("contactFormData");
    if (savedData) setFormData(JSON.parse(savedData));
  }, []);

  // Save on change
  useEffect(() => {
    localStorage.setItem("contactFormData", JSON.stringify(formData));
  }, [formData]);

  // Reset booking state after submission
  useEffect(() => {
    if (isSuccess) {
       toast.success("Booking Created successfully!");
    
      localStorage.removeItem("contactFormData");
      setFormData({
        name: "",
        email: "",
        services: [],
        urgency: "",
        budget: "",
        notes: "",
        schedule: { date: "", time: "" },
      });
      setStep(0);
      dispatch(resetState());
    } else if (isError) {
      alert("❌ Submission failed: " + message);
    }
  }, [isSuccess, isError, message, dispatch]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (steps[step].type === "checkbox") {
      setFormData((prev) => {
        const services = checked
          ? [...prev.services, value]
          : prev.services.filter((s) => s !== value);
        return { ...prev, services };
      });
    } else if (steps[step].type === "datetime") {
      const field = e.target.dataset.field;
      setFormData((prev) => ({
        ...prev,
        schedule: { ...prev.schedule, [field]: value },
      }));
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleNext = (e) => {
    if (e) e.preventDefault();

    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      // Final step → submit booking
      dispatch(createBooking(formData));
    }
  };

  const handlePrev = () => {
    if (step > 0) setStep(step - 1);
  };

  const currentStep = steps[step];

  return (
    <div className="home__wrapper">
      <Nav />
      <div className="contact-form-container">
        {/* Progress Bar */}
        <div className="progress-bar">
          <div
            className="progress"
            style={{ width: `${((step + 1) / steps.length) * 100}%` }}
          ></div>
        </div>

        {/* Form */}
        <form className="contact-form" onSubmit={handleNext}>
          <h2 className="question">{currentStep.label}</h2>

          {/* Input types */}
          {["text", "email"].includes(currentStep.type) && (
            <input
              type={currentStep.type}
              name={currentStep.name}
              value={formData[currentStep.name]}
              onChange={handleChange}
              required
              autoFocus
            />
          )}

          {currentStep.type === "checkbox" && (
            <div className="options">
              {currentStep.options.map((opt) => (
                <label
                  key={opt}
                  className={`custom-option ${
                    formData.services.includes(opt) ? "selected" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    name="services"
                    value={opt}
                    checked={formData.services.includes(opt)}
                    onChange={handleChange}
                  />
                  <span className="option-label">{opt}</span>
                </label>
              ))}
            </div>
          )}

{currentStep.type === "radio" ? (
  <div className="options">
    {currentStep.options.map((opt) => (
      <label
        key={opt}
        className={`custom-option ${
          formData[currentStep.name] === opt ? "selected" : ""
        }`}
      >
        <input
          type="checkbox"
          name={currentStep.name}
          value={opt}
          checked={formData[currentStep.name] === opt}
          onChange={handleChange}
        />
        <span className="option-label">{opt}</span>
      </label>
    ))}
  </div>
) : null}

          {currentStep.type === "textarea" && (
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
            ></textarea>
          )}

          {currentStep.type === "datetime" && (
            <div className="datetime-picker">
              <label>
                Select Date:
                <input
                  type="date"
                  data-field="date"
                  value={formData.schedule.date}
                  onChange={handleChange}
                  required
                  className="datetime-input"
                />
              </label>
              <label>
                Select Time:
                <input
                  type="time"
                  data-field="time"
                  value={formData.schedule.time}
                  onChange={handleChange}
                  required
                  className="datetime-input"
                />
              </label>
            </div>
          )}

          <div className="controls">
            {step > 0 && (
              <button type="button" className="back-btn" onClick={handlePrev}>
                Back
              </button>
            )}
            <button type="submit" className="ok-btn" disabled={isLoading}>
              {step === steps.length - 1 ? (isLoading ? "Submitting..." : "Submit") : "OK"}
            </button>
          </div>
        </form>

        <div className="nav-buttons">
          {step > 0 ? (
            <button type="button" className="nav-btn prev-btn" onClick={handlePrev}>
              <FaArrowLeft /> Prev
            </button>
          ) : (
            <span></span>
          )}

          <button
            type="button"
            onClick={handleNext}
            className="nav-btn next-btn"
            disabled={
              (currentStep.type === "text" && !formData[currentStep.name]) ||
              (currentStep.type === "email" && !formData[currentStep.name]) ||
              (currentStep.type === "checkbox" && formData.services.length === 0) ||
              (currentStep.type === "radio" && !formData[currentStep.name]) ||
              (currentStep.type === "datetime" &&
                (!formData.schedule.date || !formData.schedule.time))
            }
          >
            {step === steps.length - 1 ? "Submit" : "Next"} <FaArrowRight />
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default BookingForm;
